import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import API from "../api/axios";

const navItems = [
  { label: "Dashboard", path: "/", icon: "🏠" },
  { label: "Products", path: "/products", icon: "📋" },
  { label: "Categories", path: "/categories", icon: "🗂️" },
  { label: "Sales", path: "/sales", icon: "💰" },
  { label: "Reports", path: "/reports", icon: "📊" },
  { label: "Expenses", path: "/expenses", icon: "💸" },
  { label: "Debts", path: "/debts", icon: "💳" },
  { label: "User Guide", path: "/guide", icon: "📖" },
  { label: "Feedback", path: "/feedback", icon: "💬" },
  { label: "Subscription", path: "/subscription", icon: "💳" },
  { label: "Team", path: "/team", icon: "👥" },
  { label: "Settings", path: "/settings", icon: "⚙️" },
];

export default function Sidebar() {
  const { theme, mode } = useTheme();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [planInfo, setPlanInfo] = useState(null);
  const [lowStockCount, setLowStockCount] = useState(0);
  const [pcCollapsed, setPcCollapsed] = useState(false);
  const { logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Sidebar-specific colors derived from theme
  const sidebarBg = mode === "dark" ? "#0a0f0f" : "#ffffff";
  const sidebarBorder = mode === "dark" ? "#1e3333" : "#d0e0e0";
  const userInfoBg = mode === "dark" ? "#162424" : "#f0f8f8";
  const navActiveBg = mode === "dark" ? "#162424" : "#e0f4f4";
  const navActiveColor = "#2ec4b6";
  const navInactiveColor = theme.textSecondary;
  const hamburgerBg = mode === "dark" ? "#0f1a1a" : "#ffffff";
  const footerText = mode === "dark" ? "#3a5a5a" : "#6a9090";

  useEffect(() => {
    API.get("/products/alerts/low-stock")
      .then(res => setLowStockCount(res.data.length))
      .catch(() => {});
  }, []);

  useEffect(() => {
    const check = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (!mobile) setSidebarOpen(false);
    };
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  useEffect(() => {
    API.get("/subscriptions/trial-status")
      .then((res) => setPlanInfo(res.data))
      .catch(() => {});
  }, []);

  const getDaysLeft = () => {
    if (!planInfo) return null;
    if (planInfo.days_left !== null && planInfo.days_left !== undefined) {
      return planInfo.days_left;
    }
    if (planInfo.expires_at) {
      try {
        const expiresStr = planInfo.expires_at.replace(" ", "T");
        const expires = new Date(expiresStr);
        const now = new Date();
        const diff = Math.ceil((expires - now) / (1000 * 60 * 60 * 24));
        return Math.max(0, diff);
      } catch { return null; }
    }
    return null;
  };

  const getPlanLabel = () => {
    if (!planInfo) return "";
    const days = getDaysLeft();
    if (planInfo.status === "subscribed") {
      const name = planInfo.plan === "pro" ? "Pro Plan" : "Basic Plan";
      if (days !== null && days >= 0) return `${name} — ${days}d left`;
      return name;
    }
    if (planInfo.status === "trial") {
      return days !== null ? `Free Trial — ${days}d left` : "Free Trial";
    }
    return "Expired — Upgrade";
  };

  const getPlanColor = () => {
    if (!planInfo) return "#5a8a8a";
    if (planInfo.status === "subscribed") {
      return planInfo.plan === "pro" ? "#2ecc71" : "#2ec4b6";
    }
    if (planInfo.status === "trial") return "#f5a623";
    return "#e74c3c";
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const userName = localStorage.getItem("user_name") || "User";
  const userRole = localStorage.getItem("user_role") || "admin";

  const SidebarContent = ({ onClose }) => (
    <>
      {/* Header */}
      <div style={{
        display: "flex", alignItems: "center",
        justifyContent: "space-between",
        padding: "20px 16px 16px",
        borderBottom: `1px solid ${sidebarBorder}`,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <img
            src="/logo.png"
            alt="MartSense"
            style={{ width: "55px", height: "55px", objectFit: "contain", flexShrink: 0, marginRight: "-8px" }}
          />
          <span style={{ fontSize: "20px", fontWeight: "800", whiteSpace: "nowrap", letterSpacing: "-0.3px" }}>
            <span style={{ color: "#2ec4b6" }}>Mart</span>
            <span style={{ color: "#f6a809" }}>Sense</span>
          </span>
        </div>
        <button
          onClick={onClose}
          style={{
            background: "none", border: "none",
            color: theme.textSecondary, fontSize: "20px",
            cursor: "pointer", padding: "4px",
          }}
        >✕</button>
      </div>

      {/* User info */}
      <div style={{
        background: userInfoBg,
        borderRadius: "10px",
        padding: "12px",
        margin: "12px",
        border: `1px solid ${sidebarBorder}`,
      }}>
        <p style={{ fontSize: "10px", color: footerText, marginBottom: "4px" }}>Signed in as</p>
        <p style={{ fontSize: "14px", fontWeight: "700", color: "#2ec4b6", marginBottom: "2px" }}>
          {userName}
        </p>
        <p style={{ fontSize: "11px", color: theme.textSecondary, textTransform: "capitalize" }}>
          {userRole}
        </p>
        <p style={{ fontSize: "11px", fontWeight: "600", marginTop: "4px", color: getPlanColor() }}>
          {getPlanLabel()}
        </p>
      </div>

      {/* Navigation */}
      <nav style={{ display: "flex", flexDirection: "column", gap: "4px", flex: 1, padding: "0 8px" }}>
        {navItems.map((item) => {
          const active = location.pathname === item.path;
          const showBadge = item.path === "/" && lowStockCount > 0;
          return (
            <Link
              key={item.path}
              to={item.path}
              onClick={onClose}
              style={{
                display: "flex", alignItems: "center", gap: "12px",
                padding: "12px", borderRadius: "10px",
                textDecoration: "none", fontSize: "15px",
                fontWeight: "500", position: "relative",
                background: active ? navActiveBg : "none",
                color: active ? navActiveColor : navInactiveColor,
                transition: "background 0.2s",
              }}
            >
              <span style={{ fontSize: "18px", flexShrink: 0 }}>{item.icon}</span>
              <span>{item.label}</span>
              {showBadge && (
                <span style={{
                  position: "absolute", right: "12px", top: "50%",
                  transform: "translateY(-50%)",
                  background: "#e74c3c", color: "white",
                  borderRadius: "50%", width: "16px", height: "16px",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: "9px", fontWeight: "700",
                }}>
                  {lowStockCount}
                </span>
              )}
            </Link>
          );
        })}
      </nav>

      {/* Logout */}
      <div style={{ padding: "8px" }}>
        <button
          onClick={handleLogout}
          style={{
            display: "flex", alignItems: "center", gap: "12px",
            background: "none", border: `1px solid ${sidebarBorder}`,
            borderRadius: "10px", padding: "12px",
            color: theme.textSecondary, fontSize: "15px",
            width: "100%", cursor: "pointer",
          }}
        >
          <span>🚪</span>
          <span>Logout</span>
        </button>
      </div>

      {/* Footer */}
      <div style={{ borderTop: `1px solid ${sidebarBorder}`, padding: "12px 16px", textAlign: "center" }}>
        <p style={{ fontSize: "11px", color: footerText }}>Built by</p>
        <p style={{ fontSize: "12px", fontWeight: "700", color: "#2ec4b6", marginTop: "2px" }}>
          StackSense Technologies Ltd
        </p>
        <p style={{ fontSize: "10px", color: footerText, fontStyle: "italic", marginTop: "2px" }}>
          Navigating Technology | Delivering Intelligence
        </p>
      </div>
    </>
  );

  return (
    <>
      {/* Hamburger button — mobile closed or PC collapsed */}
      {(isMobile && !sidebarOpen) || (!isMobile && pcCollapsed) ? (
        <button
          onClick={() => isMobile ? setSidebarOpen(true) : setPcCollapsed(false)}
          style={{
            position: "fixed", top: "16px", left: "16px", zIndex: 300,
            background: hamburgerBg,
            border: `1px solid ${sidebarBorder}`,
            borderRadius: "8px", color: "#2ec4b6",
            padding: "8px 12px", fontSize: "18px", cursor: "pointer",
          }}
        >☰</button>
      ) : null}

      {/* Mobile overlay */}
      {isMobile && sidebarOpen && (
        <div
          onClick={() => setSidebarOpen(false)}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 199 }}
        />
      )}

      {/* PC transparent overlay to close on outside click */}
      {!isMobile && !pcCollapsed && (
        <div
          onClick={() => setPcCollapsed(true)}
          style={{ position: "fixed", inset: 0, zIndex: 199, background: "transparent", cursor: "default" }}
        />
      )}

      {/* Sidebar — shared for mobile and PC */}
      {(isMobile ? sidebarOpen : !pcCollapsed) && (
        <aside style={{
          position: "fixed", top: 0, left: 0,
          height: "100vh", width: "240px",
          background: sidebarBg,
          borderRight: `1px solid ${sidebarBorder}`,
          display: "flex", flexDirection: "column",
          zIndex: 200, overflowY: "auto",
          paddingBottom: "20px",
          transition: "background 0.3s ease",
          boxShadow: mode === "light" ? "2px 0 12px rgba(0,0,0,0.1)" : "none",
        }}>
          <SidebarContent onClose={() => isMobile ? setSidebarOpen(false) : setPcCollapsed(true)} />
        </aside>
      )}
    </>
     );
     }
